/**
 * Phase 1.5.3 — 顧客 LIFF キャンセル確認モーダル。
 *
 * 設計書 §7.1 の文言を元に、シンプルな日時確認 UI で実装。
 * テンプレ選択（ラジオ）+ 自由記述（任意）+ [キャンセルする] ボタン。
 *
 * モーダル表示中は body にオーバーレイを追加。実行時は cancelMyReservation API を呼び、
 * 成功すれば cancelDone 画面に遷移。
 */

import { cancelMyReservation } from "../api";
import type { MyReservation } from "../types";
import { showError } from "../render";

const TEMPLATE_OPTIONS: Array<{
  key: "customer_self" | "other_self";
  label: string;
}> = [
  { key: "customer_self", label: "お客様都合" },
  { key: "other_self", label: "その他" },
];

const CONTACT_LABELS: Record<string, string> = {
  zoom: "オンライン面談",
  line_call: "LINE通話",
};

/**
 * キャンセル確認モーダルを開く。
 */
export function openCancelConfirm(reservation: MyReservation): void {
  closeCancelConfirm(); // 念のため既存のモーダルを除去

  let selectedKey: "customer_self" | "other_self" = "customer_self";

  const backdrop = document.createElement("div");
  backdrop.id = "modal-cancel-confirm";
  backdrop.className =
    "fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4";
  backdrop.innerHTML = modalHtml(reservation);
  document.body.appendChild(backdrop);

  // 背景タップで閉じる（モーダルカード以外をタップ）
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) closeCancelConfirm();
  });

  // テンプレラジオ変更
  backdrop.querySelectorAll<HTMLInputElement>('input[name="tpl"]').forEach((radio) => {
    radio.addEventListener("change", () => {
      if (radio.checked) {
        selectedKey = radio.value as "customer_self" | "other_self";
      }
    });
  });

  // 閉じるボタン
  document.getElementById("btn-modal-back")?.addEventListener("click", () => {
    closeCancelConfirm();
  });
  document.getElementById("btn-modal-close")?.addEventListener("click", () => {
    closeCancelConfirm();
  });

  // 実行ボタン
  document
    .getElementById("btn-modal-submit")
    ?.addEventListener("click", async () => {
      const noteEl = document.getElementById(
        "input-free-note"
      ) as HTMLTextAreaElement | null;
      const freeNote = (noteEl?.value ?? "").slice(0, 200);

      const submitBtn = document.getElementById(
        "btn-modal-submit"
      ) as HTMLButtonElement | null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "実行中...";
      }

      try {
        await cancelMyReservation(reservation.id, selectedKey, freeNote);
        closeCancelConfirm();
        const { renderCancelDone } = await import("./cancelDone");
        renderCancelDone();
      } catch (e: any) {
        showError(`キャンセルに失敗しました：${e?.message ?? e}`);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = "キャンセルする";
        }
      }
    });
}

export function closeCancelConfirm(): void {
  document.getElementById("modal-cancel-confirm")?.remove();
}

function modalHtml(r: MyReservation): string {
  const dateLabel = formatDateWithWeekday(r.reservation_date);
  const contactLabel = CONTACT_LABELS[r.contact_method] ?? r.contact_method;

  return `
    <div class="bg-white rounded-lg w-full max-w-md p-5 shadow-xl"
      role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
      <div class="flex items-start justify-between mb-3">
        <h2 id="cancel-modal-title" class="text-lg font-bold text-gray-900">
          予約のキャンセル
        </h2>
        <button id="btn-modal-close"
          class="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2"
          aria-label="閉じる">
          ×
        </button>
      </div>

      <div class="text-base text-gray-800 mb-5 leading-relaxed">
        ${escapeHtml(dateLabel)} ${escapeHtml(r.reservation_time)} の<br>
        ご予約をキャンセルしますか？
        <div class="text-xs text-gray-500 mt-1">${escapeHtml(contactLabel)}</div>
      </div>

      <fieldset class="mb-4">
        <legend class="text-sm font-semibold text-gray-700 mb-2">理由（任意）</legend>
        <div class="space-y-1">
          ${TEMPLATE_OPTIONS.map(
            (opt, idx) => `
            <label class="flex items-center gap-2 text-sm">
              <input type="radio" name="tpl" value="${opt.key}"
                ${idx === 0 ? "checked" : ""}>
              <span>${escapeHtml(opt.label)}</span>
            </label>
          `
          ).join("")}
        </div>
      </fieldset>

      <div class="mb-4">
        <label for="input-free-note"
          class="block text-sm font-semibold text-gray-700 mb-1">
          キャンセル理由（任意・200字以内）
        </label>
        <textarea id="input-free-note" rows="3" maxlength="200"
          class="w-full border border-gray-300 rounded p-2 text-sm"
          placeholder="差し支えなければ理由をご記入ください"></textarea>
      </div>

      <div class="flex gap-2 justify-end">
        <button id="btn-modal-back"
          class="px-4 py-3 bg-gray-100 text-gray-700 rounded">
          キャンセルしない
        </button>
        <button id="btn-modal-submit"
          class="px-4 py-3 bg-red-600 text-white rounded font-semibold">
          キャンセルする
        </button>
      </div>
    </div>
  `;
}

function formatDateWithWeekday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${m}月${d}日(${w})`;
}

function escapeHtml(s: unknown): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
